import { Link } from 'react-router-dom'
import { LoadingScreen } from '@/components/LoadingScreen'
import { useAuth } from '@/auth/AuthContext'
import {
  useTrophyCabinet,
  type TrophyContext,
  type TrophyHonor,
  type TrophyParticipation,
  type TrophyRecord,
  type TrophySection,
} from '@/features/trophy-cabinet/api'
import { useTranslation } from '@/i18n'

const sections = [
  { key: 'football_poule', title: 'Voetbalpoules', accent: 'text-accent-300', dot: 'bg-accent-400' },
  { key: 'golero_manager', title: 'Golero Manager', accent: 'text-cyan-300', dot: 'bg-cyan-400' },
] as const

export function TrophyCabinetPage() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const query = useTrophyCabinet(Boolean(user))

  if (query.isLoading) {
    return (
      <div className="space-y-6">
        <LoadingScreen compact />
        <TrophyCabinetSkeleton />
      </div>
    )
  }

  if (query.isError || !query.data) {
    return (
      <section className="rounded-[1.5rem] border border-red-900/60 bg-red-950/30 p-5 sm:p-6">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent-300">{t('Prijzenkast')}</p>
        <h1 className="mt-3 text-2xl font-black tracking-tight text-white">{t('Prijzenkast niet geladen')}</h1>
        <p className="mt-2 text-sm leading-6 text-red-100/85">{t('Je verdiende trofeeën konden niet worden opgehaald.')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="mt-5 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500"
        >
          {t('Opnieuw proberen')}
        </button>
      </section>
    )
  }

  const cabinet = query.data.data
  const totalHonors = cabinet.summary?.honors_count ?? 0
  const totalRecords = cabinet.summary?.records_count ?? 0

  return (
    <div className="space-y-6 pb-4">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-accent-400">{t('Profiel')}</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-white sm:text-4xl">{t('Prijzenkast')}</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-surface-400 sm:text-base">
              {t('Verdiende trofeeën en persoonlijke records, per Golero-spel.')}
            </p>
          </div>
          <Link
            to="/profiel"
            className="rounded-xl border border-white/[0.1] bg-white/[0.035] px-4 py-2.5 text-sm font-bold text-surface-200 transition hover:border-white/[0.18] hover:bg-white/[0.07] hover:text-white"
          >
            {t('Terug naar profiel')}
          </Link>
        </div>
      </header>

      <section className="grid gap-px overflow-hidden rounded-[1.35rem] border border-white/[0.07] bg-white/[0.07] sm:grid-cols-2">
        <SummaryTile label={t('Verdiende trofeeën')} value={String(totalHonors)} />
        <SummaryTile label={t('Persoonlijke records')} value={String(totalRecords)} />
      </section>

      <div className="grid items-start gap-5 lg:grid-cols-2">
        {sections.map((section) => (
          <TrophySectionCard
            key={section.key}
            title={t(section.title)}
            accent={section.accent}
            dot={section.dot}
            section={cabinet[section.key]}
          />
        ))}
      </div>
    </div>
  )
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-[#0d1113] px-5 py-4 sm:px-6">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-surface-500">{label}</p>
      <p className="mt-2 text-2xl font-black text-white">{value}</p>
    </div>
  )
}

function TrophySectionCard({
  title,
  accent,
  dot,
  section,
}: {
  title: string
  accent: string
  dot: string
  section: TrophySection
}) {
  const { t } = useTranslation()
  const honoredPouleIds = new Set(
    section.honors.map((honor) => honor.context.poule?.id).filter((id): id is number => id != null),
  )
  const historicalParticipations = section.participations.filter((participation) => !honoredPouleIds.has(participation.id))
  const hasContent = historicalParticipations.length > 0 || section.honors.length > 0 || section.records.length > 0

  return (
    <section className="self-start rounded-[1.2rem] border border-white/[0.07] bg-[#0d1113] p-4 sm:p-5">
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" />
        <h2 className={`text-xs font-bold uppercase tracking-[0.18em] ${accent}`}>{title}</h2>
      </div>

      {!hasContent ? (
        <div className="mt-6 border-t border-white/[0.07] pt-5">
          <p className="text-base font-bold text-white">{t('Nog geen prijzen')}</p>
          <p className="mt-2 text-sm leading-6 text-surface-500">{t('Je behaalde trofeeën en records verschijnen hier.')}</p>
        </div>
      ) : (
        <div className="mt-5 space-y-5">
          {section.honors.some(isTrophyHonor) ? (
            <div>
              <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-surface-500">{t('Trofeeën')}</p>
              <div className="space-y-1">
                {section.honors.filter(isTrophyHonor).map((honor) => <HonorRow key={honor.id} honor={honor} />)}
              </div>
            </div>
          ) : null}
          {historicalParticipations.length > 0 || section.honors.some((honor) => !isTrophyHonor(honor)) ? (
            <div>
              <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-surface-500">{t('Eindresultaten')}</p>
              <div className="space-y-1">
                {historicalParticipations.map((participation) => <ParticipationRow key={`participation-${participation.id}`} participation={participation} />)}
                {section.honors.filter((honor) => !isTrophyHonor(honor)).map((honor) => <HonorRow key={honor.id} honor={honor} />)}
              </div>
            </div>
          ) : null}
          {section.records.length > 0 ? (
            <div>
              <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-surface-500">{t('Records')}</p>
              <div className="space-y-1">
                {section.records.map((record) => <RecordRow key={record.id} record={record} />)}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </section>
  )
}

function ParticipationRow({ participation }: { participation: TrophyParticipation }) {
  const { t } = useTranslation()
  const isOfficial = participation.classification === 'official'
  const rowClassName = 'flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 transition hover:border-white/[0.14] hover:bg-white/[0.045]'

  return (
    <Link
      to={`/poules/${participation.id}`}
      className={rowClassName}
      aria-label={`${participation.name}: ${isOfficial ? t('Officieel') : t('Niet-officieel')}`}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/[0.08] bg-white/[0.04]">
        {participation.thumbnail_url ? (
          <img src={participation.thumbnail_url} alt="" className="h-7 w-7 object-contain" loading="lazy" />
        ) : (
          <span className="text-xs font-black text-surface-500">G</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-bold text-white">{participation.name}</h3>
        {participation.season?.name ? <p className="mt-0.5 truncate text-xs font-semibold text-surface-500">{participation.season.name}</p> : null}
        <span
          className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${
            isOfficial
              ? 'border-cyan-300/20 bg-cyan-300/[0.08] text-cyan-200'
              : 'border-white/[0.12] bg-white/[0.04] text-surface-400'
          }`}
        >
          {isOfficial ? t('Officieel') : t('Niet-officieel')}
        </span>
      </div>
      {participation.final_rank != null ? (
        <div className="shrink-0 text-right">
          <p className="text-sm font-black text-accent-300">#{participation.final_rank}</p>
          {participation.participant_count != null ? <p className="mt-0.5 text-[10px] font-semibold text-surface-500">{t('van {count}', { count: participation.participant_count })}</p> : null}
        </div>
      ) : null}
      <span className="shrink-0 text-lg leading-none text-surface-500" aria-hidden="true">→</span>
    </Link>
  )
}

function HonorRow({ honor }: { honor: TrophyHonor }) {
  const { t } = useTranslation()
  const title = formatHonorTitle(honor, t)
  const subtitle = formatHonorSubtitle(honor)
  const context = [title, subtitle].filter(Boolean).join(' · ')
  const logoUrl = honor.context.poule?.logo_url ?? honor.context.fantasy_league?.logo_url ?? null
  const pouleId = honor.context.poule?.id
  const rowClassName = 'flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 transition hover:border-white/[0.14] hover:bg-white/[0.045]'

  const content = (
    <>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/[0.08] bg-white/[0.04]">
        {logoUrl ? <img src={logoUrl} alt="" className="h-7 w-7 object-contain" loading="lazy" /> : <span className="text-xs font-black text-surface-500">G</span>}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-bold text-white">{title}</h3>
        {subtitle ? <p className="mt-0.5 truncate text-xs font-semibold text-surface-500">{subtitle}</p> : null}
      </div>
      <PlacementSummary honor={honor} t={t} />
    </>
  )

  return pouleId != null ? (
    <Link to={`/poules/${pouleId}`} className={rowClassName} aria-label={`${honor.title}: ${context || t('Poule')}`}>
      {content}
    </Link>
  ) : (
    <article className={rowClassName}>{content}</article>
  )
}

function PlacementSummary({
  honor,
  t,
}: {
  honor: TrophyHonor
  t: (key: string, values?: Record<string, string | number>) => string
}) {
  const participantCount = honor.participant_count ?? honor.context.participant_count ?? null

  return (
    <div className="shrink-0 text-right">
      {honor.rank != null || isWinnerHonor(honor) ? (
        <div className="flex items-center justify-end gap-1 text-sm font-black text-accent-300">
          {isWinnerHonor(honor) ? <span className="text-sm leading-none text-amber-300" aria-label={t('Winnaar')}>🏆</span> : null}
          {honor.rank != null ? <span>#{honor.rank}</span> : null}
        </div>
      ) : null}
      {participantCount != null ? (
        <p className="mt-0.5 text-[10px] font-semibold text-surface-500">
          {t('van {count}', { count: participantCount })}
        </p>
      ) : null}
    </div>
  )
}

function isTrophyHonor(honor: TrophyHonor) {
  return honor.rank == null || honor.rank === 1 || honor.honor_type.endsWith('_winner')
}

function RecordRow({ record }: { record: TrophyRecord }) {
  const { t } = useTranslation()
  const context = formatContext(record.context)
  const value = record.value_label || (record.value == null ? '—' : String(record.value))

  return (
    <article className="flex items-start justify-between gap-3 border-t border-white/[0.07] pt-3 first:border-t-0 first:pt-0">
      <div className="min-w-0">
        <h3 className="text-sm font-bold text-white">{humanizeKey(record.record_type)}</h3>
        {context ? <p className="mt-1 text-xs font-semibold text-surface-500">{context}</p> : null}
      </div>
      <span className="shrink-0 text-sm font-black text-accent-300">{value}</span>
    </article>
  )
}

function formatContext(context: TrophyContext) {
  const item = context.poule ?? context.fantasy_league ?? context.fantasy_subleague ?? context.fantasy_challenge ?? context.season ?? context.app_round
  return [item?.name?.trim(), context.season?.name?.trim()]
    .filter((value, index, values) => value && values.indexOf(value) === index)
    .join(' · ')
}

function formatHonorTitle(
  honor: TrophyHonor,
  t: (key: string, values?: Record<string, string | number>) => string,
) {
  const pouleName = honor.context.poule?.name?.trim()
  if (pouleName) return pouleName

  switch (honor.honor_type) {
    case 'official_manager_league_winner':
    case 'manager_league_winner':
      return t('Manager League-winnaar')
    case 'official_manager_league_result':
    case 'manager_league_result':
      return t('Manager League-eindresultaat')
    case 'manager_challenge_winner':
      return t('Manager Challenge-winnaar')
    case 'manager_challenge_result':
      return t('Manager Challenge-eindresultaat')
    default:
      return honor.title
  }
}

function formatHonorSubtitle(honor: TrophyHonor) {
  if (honor.context.poule) return honor.context.season?.name?.trim() || null
  return formatContext(honor.context)
}

function isWinnerHonor(honor: TrophyHonor) {
  return honor.rank === 1 || honor.honor_type.endsWith('_winner')
}

function humanizeKey(value: string) {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function TrophyCabinetSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="space-y-3">
        <div className="h-3 w-24 rounded bg-white/[0.08]" />
        <div className="h-10 w-64 rounded bg-white/[0.08]" />
        <div className="h-4 w-96 max-w-full rounded bg-white/[0.06]" />
      </div>
      <div className="grid gap-px overflow-hidden rounded-[1.35rem] border border-white/[0.07] bg-white/[0.07] sm:grid-cols-2">
        <div className="h-24 bg-[#0d1113]" />
        <div className="h-24 bg-[#0d1113]" />
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        {[1, 2, 3].map((item) => <div key={item} className="h-52 rounded-[1.35rem] border border-white/[0.07] bg-[#0d1113]" />)}
      </div>
    </div>
  )
}
